import { prisma, TemplateV2Level, TemplateV2Type, type Prisma } from "client";
import { EVENT_TIME_MOMENTS, SESSION_MOMENTS } from "./schedule.pure";
import {
  emptyCommunicationLogStats,
  statsFromEmailAgg,
  statsFromPushAgg,
} from "./stats.pure";
import { getCommunicationBroadcastProgress } from "../communicationBroadcast";

export {
  ATTENDEE_EVENT_MOMENTS,
  EVENT_TIME_MOMENTS,
  SESSION_MOMENTS,
  applyOffset,
  calculateRelativeSendAt,
  isAttendeeEventMoment,
  isEventTimeMoment,
  isRelativeSendDue,
  isSessionMoment,
  offsetMilliseconds,
  type CommunicationOffsetUnitName,
  type CommunicationTriggerMomentName,
} from "./schedule.pure";
export {
  aggregateCommunicationLogs,
  emptyCommunicationLogStats,
  statsFromEmailAgg,
  statsFromPushAgg,
  type CommunicationLogStats,
  type LeanCommunicationLog,
} from "./stats.pure";

const communicationLogFields = {
  id: true,
  status: true,
  stats: true,
  provider: true,
  failedReason: true,
  createdAt: true,
  updatedAt: true,
  email: true,
  phoneNumber: true,
  recipientProfileId: true,
  providerMessageId: true,
} as const;

const communicationSettingsListSelect = {
  id: true,
  name: true,
  status: true,
  sendAt: true,
  triggerMoment: true,
  audienceSize: true,
  templateV2Id: true,
  createdAt: true,
  templateV2: {
    select: {
      id: true,
      name: true,
      type: true,
    },
  },
  broadcastDispatches: {
    select: {
      id: true,
      status: true,
      expectedCount: true,
      queuedCount: true,
      terminalCount: true,
      inAppSucceededCount: true,
      inAppFailedCount: true,
      expoAcceptedCount: true,
      expoFailedCount: true,
      expoNoTokenCount: true,
      failedReason: true,
    },
    orderBy: { createdAt: "desc" as const },
    take: 1,
  },
} as const;

const DEFAULT_LIST_LIMIT = 10;

function parseListPagination(page: unknown, limit: unknown) {
  const safePage =
    Number.isFinite(Number(page)) && Number(page) >= 1
      ? Math.floor(Number(page))
      : 1;
  const safeLimit =
    Number.isFinite(Number(limit)) && Number(limit) >= 1
      ? Math.min(100, Math.floor(Number(limit)))
      : DEFAULT_LIST_LIMIT;
  return { safePage, safeLimit };
}

// Reusable include object for communication settings queries (optimized for minimal data)
const communicationSettingsInclude = {
  templateV2: {
    select: {
      id: true,
      name: true,
      type: true,
      scopeId: true,
      level: true,
      config: true,
    },
  },
  triggerSession: {
    select: {
      id: true,
      startDate: true,
      eventId: true,
    },
  },
  audienceProfiles: {
    select: {
      id: true,
      name: true,
      email: true,
      phoneNumber: true,
    },
  },
};

function asObjectId(id: string) {
  return { $oid: id };
}

function settingsIdFromAgg(value: unknown): string {
  if (typeof value === "string") return value;
  if (value && typeof value === "object" && "$oid" in value) {
    return String((value as { $oid: string }).$oid);
  }
  return "";
}

type EmailAggRow = {
  _id?: unknown;
  allSent?: number;
  failed?: number;
  totalOpens?: number;
  totalClicks?: number;
  bounced?: number;
  delivered?: number;
  spam?: number;
  openedCount?: number;
  clickedCount?: number;
};

type PushAggRow = {
  _id?: unknown;
  allSent?: number;
  delivered?: number;
  failed?: number;
};

async function aggregateEmailLogStats(ids: string[]): Promise<EmailAggRow[]> {
  const raw = await prisma.communicationLog.aggregateRaw({
    pipeline: [
      { $match: { communicationSettingsId: { $in: ids.map(asObjectId) } } },
      {
        $group: {
          _id: "$communicationSettingsId",
          allSent: {
            $sum: {
              $cond: [
                {
                  $or: [
                    { $eq: ["$status", "SENT"] },
                    { $gt: [{ $ifNull: ["$stats.delivered", 0] }, 0] },
                  ],
                },
                1,
                0,
              ],
            },
          },
          failed: {
            $sum: { $cond: [{ $eq: ["$status", "FAILED"] }, 1, 0] },
          },
          totalOpens: { $sum: { $ifNull: ["$stats.opened", 0] } },
          totalClicks: { $sum: { $ifNull: ["$stats.clicked", 0] } },
          bounced: { $sum: { $ifNull: ["$stats.bounced", 0] } },
          delivered: { $sum: { $ifNull: ["$stats.delivered", 0] } },
          spam: { $sum: { $ifNull: ["$stats.spam", 0] } },
          openedKeys: {
            $addToSet: {
              $cond: [
                { $gt: [{ $ifNull: ["$stats.opened", 0] }, 0] },
                { $ifNull: ["$recipientProfileId", "$email"] },
                "$$REMOVE",
              ],
            },
          },
          clickedKeys: {
            $addToSet: {
              $cond: [
                { $gt: [{ $ifNull: ["$stats.clicked", 0] }, 0] },
                { $ifNull: ["$recipientProfileId", "$email"] },
                "$$REMOVE",
              ],
            },
          },
        },
      },
      {
        $project: {
          allSent: 1,
          failed: 1,
          totalOpens: 1,
          totalClicks: 1,
          bounced: 1,
          delivered: 1,
          spam: 1,
          openedCount: { $size: "$openedKeys" },
          clickedCount: { $size: "$clickedKeys" },
        },
      },
    ],
  });
  return (Array.isArray(raw) ? raw : []) as EmailAggRow[];
}

async function aggregatePushLogStats(ids: string[]): Promise<PushAggRow[]> {
  const raw = await prisma.communicationLog.aggregateRaw({
    pipeline: [
      { $match: { communicationSettingsId: { $in: ids.map(asObjectId) } } },
      {
        $group: {
          _id: {
            settingsId: "$communicationSettingsId",
            recipient: { $ifNull: ["$recipientProfileId", "$email"] },
          },
          inAppOk: {
            $max: {
              $cond: [
                {
                  $and: [
                    { $eq: ["$provider", "IN_APP"] },
                    { $in: ["$status", ["DELIVERED", "SENT"]] },
                  ],
                },
                1,
                0,
              ],
            },
          },
          expoOk: {
            $max: {
              $cond: [
                {
                  $and: [
                    { $eq: ["$provider", "EXPO"] },
                    { $eq: ["$status", "SENT"] },
                  ],
                },
                1,
                0,
              ],
            },
          },
          expoFailed: {
            $max: {
              $cond: [
                {
                  $and: [
                    { $eq: ["$provider", "EXPO"] },
                    { $eq: ["$status", "FAILED"] },
                  ],
                },
                1,
                0,
              ],
            },
          },
        },
      },
      {
        $group: {
          _id: "$_id.settingsId",
          allSent: { $sum: "$expoOk" },
          delivered: { $sum: "$inAppOk" },
          failed: { $sum: "$expoFailed" },
        },
      },
    ],
  });
  return (Array.isArray(raw) ? raw : []) as PushAggRow[];
}

async function loadLogStatsBySettingsIds(
  ids: string[],
  typeById: Map<string, string | null | undefined>
) {
  const statsById = new Map(
    ids.map((id) => [id, emptyCommunicationLogStats()] as const)
  );
  if (ids.length === 0) return statsById;

  const pushIds = ids.filter(
    (id) => typeById.get(id) === TemplateV2Type.NOTIFICATION
  );
  const emailIds = ids.filter(
    (id) => typeById.get(id) !== TemplateV2Type.NOTIFICATION
  );

  // ponytail: $match on communicationSettingsId uses
  // @@index([communicationSettingsId, status, createdAt]). $addToSet of
  // recipient ids is the ceiling — persist rollup counters if a campaign
  // regularly exceeds ~20k unique recipients.
  const [emailRows, pushRows] = await Promise.all([
    emailIds.length ? aggregateEmailLogStats(emailIds) : [],
    pushIds.length ? aggregatePushLogStats(pushIds) : [],
  ]);
  for (const row of emailRows) {
    const id = settingsIdFromAgg(row._id);
    if (id) statsById.set(id, statsFromEmailAgg(row));
  }
  for (const row of pushRows) {
    const id = settingsIdFromAgg(row._id);
    if (id) statsById.set(id, statsFromPushAgg(row));
  }
  return statsById;
}

// CREATE

export const createCommunicationSettings = async (
  data: Prisma.CommunicationSettingsUncheckedCreateInput
) => {
  return await prisma.communicationSettings.create({ data });
};

// GET

export const getCommunicationSettings = async (id: string) => {
  const [settings, broadcastProgress] = await Promise.all([
    prisma.communicationSettings.findUnique({
      where: { id },
      include: communicationSettingsInclude,
    }),
    getCommunicationBroadcastProgress(id),
  ]);
  if (!settings) return settings;
  const statsById = await loadLogStatsBySettingsIds(
    [settings.id],
    new Map([[settings.id, settings.templateV2?.type]])
  );
  return {
    ...settings,
    broadcastProgress,
    logStats: statsById.get(settings.id) ?? emptyCommunicationLogStats(),
  };
};

async function attachLogStats<
  T extends {
    id: string;
    templateV2?: { type?: string | null } | null;
    broadcastDispatches?: Array<{
      expectedCount: number;
      terminalCount: number;
      [key: string]: unknown;
    }>;
  }
>(settings: T[]) {
  if (settings.length === 0) return settings;
  const statsById = await loadLogStatsBySettingsIds(
    settings.map((row) => row.id),
    new Map(settings.map((row) => [row.id, row.templateV2?.type]))
  );
  return settings.map((row) => {
    const broadcastProgress = row.broadcastDispatches?.[0];
    return {
      ...row,
      broadcastDispatches: undefined,
      broadcastProgress: broadcastProgress
        ? {
            ...broadcastProgress,
            unaccounted: Math.max(
              0,
              broadcastProgress.expectedCount - broadcastProgress.terminalCount
            ),
          }
        : null,
      logStats: statsById.get(row.id) ?? emptyCommunicationLogStats(),
    };
  });
}

export async function getCommunicationSettingsByEventId(
  eventId: string,
  options?: { page?: number; limit?: number; templateIds?: string[] }
) {
  const eventTemplates = await prisma.templateV2.findMany({
    where: {
      level: TemplateV2Level.EVENT,
      scopeId: eventId,
    },
    select: {
      id: true,
      name: true,
      type: true,
    },
  });
  const eventTemplateIds = eventTemplates.map((template) => template.id);
  const campaignTemplateIds = await prisma.communicationSettings.findMany({
    where: { templateV2Id: { in: eventTemplateIds } },
    select: { templateV2Id: true },
    distinct: ["templateV2Id"],
  });
  const allowedTemplateIds = new Set(
    campaignTemplateIds.flatMap((campaign) =>
      campaign.templateV2Id ? [campaign.templateV2Id] : []
    )
  );
  const filterTemplates = eventTemplates.filter((template) =>
    allowedTemplateIds.has(template.id)
  );
  const templateIds = options?.templateIds
    ? options.templateIds.filter((id) => allowedTemplateIds.has(id))
    : [...allowedTemplateIds];
  const where = { templateV2Id: { in: templateIds } };

  if (options) {
    const { safePage, safeLimit } = parseListPagination(
      options.page ?? 1,
      options.limit ?? DEFAULT_LIST_LIMIT
    );
    if (templateIds.length === 0) {
      return {
        communicationSettings: [],
        pagination: {
          total: 0,
          page: safePage,
          limit: safeLimit,
          totalPages: 1,
        },
        filterTemplates,
      };
    }
    const [total, settings] = await Promise.all([
      prisma.communicationSettings.count({ where }),
      prisma.communicationSettings.findMany({
        where,
        select: communicationSettingsListSelect,
        orderBy: { createdAt: "desc" },
        skip: (safePage - 1) * safeLimit,
        take: safeLimit,
      }),
    ]);
    const communicationSettings = await attachLogStats(settings);
    return {
      communicationSettings,
      pagination: {
        total,
        page: safePage,
        limit: safeLimit,
        totalPages: Math.max(1, Math.ceil(total / safeLimit)),
      },
      filterTemplates,
    };
  }

  const settings = await prisma.communicationSettings.findMany({
    where,
    select: communicationSettingsListSelect,
    orderBy: { createdAt: "desc" },
  });
  return attachLogStats(settings);
}

export async function getCommunicationSettingsReport(
  id: string,
  page: number,
  limit: number
) {
  const settings = await prisma.communicationSettings.findUnique({
    where: { id },
    select: {
      id: true,
      name: true,
      status: true,
      sendAt: true,
      createdAt: true,
      updatedAt: true,
      audienceProfileIds: true,
      templateV2: { select: { type: true } },
    },
  });
  if (!settings) return null;

  const safePage = Number.isFinite(page) && page >= 1 ? Math.floor(page) : 1;
  const safeLimit =
    Number.isFinite(limit) && limit >= 1 ? Math.min(50, Math.floor(limit)) : 8;
  const total = settings.audienceProfileIds.length;
  const pageIds = settings.audienceProfileIds.slice(
    (safePage - 1) * safeLimit,
    safePage * safeLimit
  );

  const profiles = pageIds.length
    ? await prisma.profile.findMany({
        where: { id: { in: pageIds } },
        select: {
          id: true,
          name: true,
          email: true,
          phoneNumber: true,
        },
      })
    : [];
  const pageEmails = [
    ...new Set(
      profiles.flatMap((profile) => {
        const email = profile.email?.trim();
        return email ? [email, email.toLowerCase()] : [];
      })
    ),
  ];
  const logs = pageIds.length
    ? await prisma.communicationLog.findMany({
        where: {
          communicationSettingsId: id,
          OR: [
            { recipientProfileId: { in: pageIds } },
            ...(pageEmails.length ? [{ email: { in: pageEmails } }] : []),
          ],
        },
        select: communicationLogFields,
        orderBy: { createdAt: "desc" },
      })
    : [];

  const profileById = new Map(profiles.map((profile) => [profile.id, profile]));
  const rows = pageIds.map((profileId) => {
    const profile = profileById.get(profileId);
    const email = profile?.email?.trim().toLowerCase();
    const profileLogs = logs.filter(
      (log) =>
        log.recipientProfileId === profileId ||
        (email && log.email?.trim().toLowerCase() === email)
    );
    return {
      profile: profile ?? {
        id: profileId,
        name: null,
        email: null,
        phoneNumber: null,
      },
      logs: profileLogs,
    };
  });

  return {
    id: settings.id,
    name: settings.name,
    status: settings.status,
    sendAt: settings.sendAt,
    createdAt: settings.createdAt,
    updatedAt: settings.updatedAt,
    isPush: settings.templateV2?.type === TemplateV2Type.NOTIFICATION,
    rows,
    pagination: {
      total,
      page: safePage,
      limit: safeLimit,
      totalPages: Math.max(1, Math.ceil(total / safeLimit)),
    },
  };
}

// UPDATE

export const updateCommunicationSettings = async (
  id: string,
  data: Prisma.CommunicationSettingsUncheckedUpdateInput
) => {
  return await prisma.communicationSettings.update({
    where: { id },
    data,
    include: communicationSettingsInclude,
  });
};

// DELETE

export const deleteCommunicationSettings = async (id: string) => {
  return await prisma.communicationSettings.delete({ where: { id } });
};

const FINITE_RELATIVE_MOMENTS = [...SESSION_MOMENTS, ...EVENT_TIME_MOMENTS];

// GET scheduled communications that are due for sending
export const getScheduledCommunicationsDue = async () => {
  const now = new Date();
  return await prisma.communicationSettings.findMany({
    where: {
      status: "SCHEDULED",
      OR: [
        {
          sendingType: { in: ["NOW", "SCHEDULED"] },
          sendAt: { lte: now },
        },
        {
          sendingType: "RELATIVE_TO_EVENT",
          triggerMoment: { in: [...FINITE_RELATIVE_MOMENTS] },
        },
      ],
    },
    include: {
      templateV2: {
        select: {
          id: true,
          type: true,
          trigger: true,
          scopeId: true,
          level: true,
          html: true,
          config: true,
        },
      },
      triggerSession: {
        select: { id: true, startDate: true, eventId: true },
      },
    },
    orderBy: { sendAt: "asc" },
  });
};

// UPDATE communication settings status (helper for simple status updates)
export const updateCommunicationSettingsStatus = async (
  id: string,
  status: "SCHEDULED" | "SENT" | "FAILED" | "PROCESSING"
) => {
  return await prisma.communicationSettings.update({
    where: { id },
    data: { status },
  });
};

export async function persistResolvedAudience(
  id: string,
  profileIds: string[]
) {
  return prisma.communicationSettings.update({
    where: { id },
    data: {
      audienceProfileIds: profileIds,
      audienceSize: profileIds.length,
      audienceResolvedAt: new Date(),
    },
  });
}

export async function rollupFiniteCommunicationStatus(
  id: string
): Promise<"SENT" | "FAILED" | "PROCESSING" | "SCHEDULED"> {
  const settings = await prisma.communicationSettings.findUnique({
    where: { id },
    select: {
      sendingType: true,
      triggerMoment: true,
      audienceProfileIds: true,
      communicationLogs: {
        select: {
          recipientProfileId: true,
          occurrenceKey: true,
          status: true,
        },
      },
    },
  });
  if (!settings) return "FAILED";
  if (
    settings.triggerMoment === "ON_CHECK_IN" ||
    settings.triggerMoment === "ON_ORDER_COMPLETED"
  ) {
    return "SCHEDULED";
  }

  const expected = new Set(settings.audienceProfileIds);
  if (expected.size === 0) return "FAILED";

  const terminals = new Map<string, "SENT" | "FAILED">();
  for (const log of settings.communicationLogs) {
    if (!log.recipientProfileId) continue;
    const key = `${log.recipientProfileId}:${log.occurrenceKey ?? "default"}`;
    if (log.status === "DELIVERED" || log.status === "SENT") {
      terminals.set(key, "SENT");
    } else if (log.status === "FAILED" && !terminals.has(key)) {
      terminals.set(key, "FAILED");
    }
  }

  const completed = [...expected].every((profileId) =>
    [...terminals.keys()].some((key) => key.startsWith(`${profileId}:`))
  );
  if (!completed) return "PROCESSING";

  const anyFailed = [...terminals.values()].includes("FAILED");
  const status = anyFailed ? "FAILED" : "SENT";
  await updateCommunicationSettingsStatus(id, status);
  return status;
}
